/**
 * Helper compartido para subir el video de inspección de una recepción
 * directamente (sin entrar al detalle), gobernado por el permiso
 * `recepciones:subir_video`.
 *
 * Usado desde el kebab ⋮ del listado general de Recepciones y del detalle
 * de OC, para que la persona cuya única tarea es subir el video no tenga
 * que recorrer todas las secciones del formulario de recepción.
 */

import { pickVideo } from './mediaChooser.js';
import recepcionService from '../services/recepcion.service.js';
import toast from './toast.js';
import modal from './modal.js';

/**
 * Confirmación estilizada (modal.open) para reemplazar un video existente.
 * `dismissable: false` fuerza a elegir uno de los dos botones, así la
 * promesa siempre resuelve (no queda colgada si cierran con X o clic afuera).
 * @returns {Promise<boolean>} true si el usuario confirma el reemplazo
 */
function confirmarReemplazoVideo() {
  return new Promise((resolve) => {
    modal.open(
      'Reemplazar video',
      '<p>Esta recepción ya tiene un video. ¿Deseas reemplazarlo? El video actual se eliminará y no se podrá recuperar.</p>',
      [
        { text: 'Cancelar', class: 'btn-secondary', onClick: () => resolve(false) },
        { text: 'Reemplazar', class: 'btn-danger', onClick: () => resolve(true) }
      ],
      { dismissable: false }
    );
  });
}

function getVideoDuration(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = function () {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };
    video.onerror = function () {
      reject(new Error('Error al cargar el video'));
    };
    video.src = URL.createObjectURL(file);
  });
}

/**
 * Valida un archivo de video de inspección contra las reglas de negocio
 * (formato MP4/MOV/AVI, tamaño ≤100MB, duración 30s–5min). Es la ÚNICA
 * fuente de verdad de estas reglas, compartida por el flujo de subida
 * directa (`subirVideoRecepcion`) y el de creación de recepción
 * (`NuevaRecepcion.pickVideoInspeccion`), donde el video se difiere.
 *
 * @param {File} file
 * @returns {Promise<{ ok: boolean, duration?: number, error?: string }>}
 */
export async function validarVideoInspeccion(file) {
  const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
  if (!validTypes.includes(file.type)) {
    return { ok: false, error: 'Formato de video no válido. Use MP4, MOV o AVI' };
  }
  if (file.size > 100 * 1024 * 1024) {
    return { ok: false, error: 'El video supera el tamaño máximo de 100MB' };
  }

  let duration;
  try {
    duration = await getVideoDuration(file);
  } catch {
    return { ok: false, error: 'Error al cargar el video' };
  }
  if (duration < 30) {
    return { ok: false, error: 'El video debe durar mínimo 30 segundos' };
  }
  if (duration > 300) {
    return { ok: false, error: 'El video debe durar máximo 5 minutos' };
  }

  return { ok: true, duration };
}

/**
 * Abre el selector de video, valida (vía `validarVideoInspeccion`),
 * lo sube a GCS y lo persiste en la recepción vía el endpoint dedicado
 * (`PATCH /almacen/recepciones/:id/video`, permiso `subir_video`).
 *
 * @param {number} recepcionId
 * @param {string} numSerie
 * @param {{ onProgress?: (msg: string) => void, existeVideo?: boolean }} [opts]
 *   existeVideo: si la recepción ya tiene video, se pide confirmación antes
 *   de reemplazarlo (el video viejo se borra de GCS en el backend).
 * @returns {Promise<boolean>} true si se subió correctamente
 */
export async function subirVideoRecepcion(recepcionId, numSerie, { onProgress, existeVideo = false } = {}) {
  if (existeVideo && !(await confirmarReemplazoVideo())) {
    return false;
  }

  const file = await pickVideo();
  if (!file) return false;

  const { ok, error } = await validarVideoInspeccion(file);
  if (!ok) {
    toast.error(error);
    return false;
  }

  try {
    onProgress?.('Subiendo video de inspección...');
    const ext = file.name.split('.').pop();
    const url = await recepcionService.subirVideo(file, `${numSerie}_inspeccion.${ext}`, numSerie);
    onProgress?.('Guardando video...');
    await recepcionService.actualizarVideo(recepcionId, url);
    toast.success('Video subido exitosamente');
    return true;
  } catch (error) {
    console.error('Error al subir video:', error);
    toast.error(error.message || 'Error al subir el video');
    return false;
  }
}
